/**
 * Promotions list -- phase 6b, Task 2. `/discounts/promotions`.
 *
 * A server-paged DataGrid over GET /admin/discounts/promotions (every kind;
 * ADMIN_API.md P6-1) with kind / active / name-search filters. MANUAL rows
 * carry the write actions (Edit, Activate/Deactivate, Delete -- write role
 * only, `useCanWriteDiscounts`); the seeded singletons (REFERRAL,
 * REFERRAL_REWARD, LOYALTY_REWARD) show a "Managed on Loyalty/Referrals"
 * chip instead, because the API refuses to edit them here (422/409) -- they
 * are edited on the Loyalty and Referrals tabs.
 *
 * Edit fetches GET /promotions/:id first (the list row lacks the stacking
 * detail the dialog needs) through the query cache, so the detail screen
 * (Task 3) finds it warm.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, PauseCircle as PauseIcon, PlayCircle as PlayIcon } from '@mui/icons-material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { KIND_LABEL, discountLabel, fmtDateTime, managedOn } from '../format';
import { gridTestProps } from '../gridTestProps';
import { useCanWriteDiscounts } from '../roles';
import { useDebounced } from '../useDebounced';
import { useScrollToListTop } from '../../../hooks/useScrollToListTop';
import type { ListPromotionsQuery, PromotionDetail, PromotionKind, PromotionRow } from '../types';
import { PromotionDialog } from './PromotionDialog';

type ActiveFilter = 'all' | 'active' | 'inactive';

type Confirm = { kind: 'toggle'; row: PromotionRow } | { kind: 'delete'; row: PromotionRow } | null;

export const PromotionsList: React.FC = () => {
  const notify = useNotify();
  const qc = useQueryClient();
  const canWrite = useCanWriteDiscounts();

  const [kind, setKind] = useState<PromotionKind | ''>('');
  const [active, setActive] = useState<ActiveFilter>('all');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const listTopRef = useScrollToListTop(pagination.page);

  // Any filter change goes back to the first page.
  useEffect(() => {
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [kind, active, q]);

  const query = useMemo<ListPromotionsQuery>(
    () => ({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      kind: kind || undefined,
      active: active === 'all' ? undefined : active === 'active',
      q: q || undefined,
    }),
    [pagination, kind, active, q],
  );

  const list = useQuery({
    queryKey: qk.promotions(query),
    queryFn: () => discountsApi.listPromotions(query),
    placeholderData: keepPreviousData,
  });

  const [dialog, setDialog] = useState<{ open: boolean; initial?: PromotionDetail }>({ open: false });
  const [loadingEditId, setLoadingEditId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);

  // Whole ['discounts'] prefix after any write: every list, the detail (Task 3), vouchers, stats bar.
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.all });

  // Stable (qc and react-admin's notify are both stable), so the columns memo
  // below can list it honestly instead of silencing exhaustive-deps.
  const openEdit = useCallback(
    async (row: PromotionRow) => {
      setLoadingEditId(row.id);
      try {
        const detail = await qc.fetchQuery({ queryKey: qk.promotion(row.id), queryFn: () => discountsApi.getPromotion(row.id) });
        setDialog({ open: true, initial: detail });
      } catch (err) {
        notify(toApiFailure(err).message, { type: 'error' });
      } finally {
        setLoadingEditId(null);
      }
    },
    [qc, notify],
  );

  const toggle = useMutation({
    mutationFn: (row: PromotionRow) => discountsApi.setPromotionActive(row.id, !row.active),
    onSuccess: async (saved) => {
      await invalidate();
      notify(saved.active ? `${saved.name} activated` : `${saved.name} deactivated`, { type: 'success' });
    },
    onError: (err) => notify(toApiFailure(err).message, { type: 'error' }),
    onSettled: () => setConfirm(null),
  });

  const remove = useMutation({
    mutationFn: (row: PromotionRow) => discountsApi.deletePromotion(row.id),
    onSuccess: async (_v, row) => {
      await invalidate();
      notify(`${row.name} deleted`, { type: 'success' });
    },
    // A 409 (has confirmed redemptions / non-MANUAL) carries the API's userMessage verbatim.
    onError: (err) => notify(toApiFailure(err).message, { type: 'error' }),
    onSettled: () => setConfirm(null),
  });

  const columns = useMemo<GridColDef<PromotionRow>[]>(() => {
    const cols: GridColDef<PromotionRow>[] = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        // Room for the longest seeded name ("Dee Referral Coupons") AND the
        // "Managed on Referrals" chip: with the chip allowed to shrink the
        // link lost the fight and every seeded row read "Dee R…" (2026-09-19).
        minWidth: 340,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Link component={RouterLink} to={`/discounts/promotions/${row.id}`} underline="hover" noWrap title={row.name}>
              {row.name}
            </Link>
            {managedOn(row.kind) && (
              <Chip label={managedOn(row.kind)} size="small" variant="outlined" color="info" sx={{ flexShrink: 0 }} />
            )}
          </Stack>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 140,
        renderCell: ({ value }) => <Chip label={KIND_LABEL[value as PromotionKind] ?? value} size="small" />,
      },
      { field: 'discount', headerName: 'Discount', width: 130, valueGetter: (_v, row) => discountLabel(row) },
      {
        field: 'active',
        headerName: 'Active',
        width: 100,
        renderCell: ({ value }) => <Chip label={value ? 'Active' : 'Inactive'} size="small" color={value ? 'success' : 'default'} />,
      },
      {
        field: 'codes',
        headerName: 'Codes',
        width: 100,
        description: 'active / total voucher codes',
        valueGetter: (_v, row) => `${row.activeVoucherCount}/${row.voucherCount}`,
      },
      { field: 'confirmedRedemptionCount', headerName: 'Redemptions', width: 120, type: 'number' },
      { field: 'startsAt', headerName: 'Starts', width: 170, valueGetter: (v: string | null) => fmtDateTime(v) },
      { field: 'endsAt', headerName: 'Ends', width: 170, valueGetter: (v: string | null) => fmtDateTime(v) },
    ];
    if (canWrite) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) =>
          row.kind === 'MANUAL' ? (
            <Stack direction="row" spacing={0}>
              <Tooltip title="Edit">
                <span>
                  <IconButton size="small" aria-label={`Edit ${row.name}`} onClick={() => openEdit(row)} disabled={loadingEditId === row.id}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={row.active ? 'Deactivate' : 'Activate'}>
                <IconButton
                  size="small"
                  aria-label={row.active ? `Deactivate ${row.name}` : `Activate ${row.name}`}
                  onClick={() => setConfirm({ kind: 'toggle', row })}
                >
                  {row.active ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" aria-label={`Delete ${row.name}`} color="error" onClick={() => setConfirm({ kind: 'delete', row })}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null,
      });
    }
    return cols;
  }, [canWrite, loadingEditId, openEdit]);

  const busy = toggle.isPending || remove.isPending;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <TextField
          label="Search"
          placeholder="Name contains…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 80, 'aria-label': 'Search promotions' } }}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          label="Kind"
          size="small"
          value={kind}
          onChange={(e) => setKind(e.target.value as PromotionKind | '')}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">All kinds</MenuItem>
          {(Object.keys(KIND_LABEL) as PromotionKind[]).map((k) => (
            <MenuItem key={k} value={k}>
              {KIND_LABEL[k]}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={active}
          onChange={(_e, v: ActiveFilter | null) => v && setActive(v)}
          aria-label="Active filter"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true })}>
            New promotion
          </Button>
        )}
      </Stack>

      {list.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load promotions: {toApiFailure(list.error).message}
        </Alert>
      )}

      <Box ref={listTopRef} sx={{ width: '100%' }}>
        <DataGrid
          rows={list.data?.data ?? []}
          columns={columns}
          loading={list.isFetching}
          paginationMode="server"
          rowCount={list.data?.total ?? 0}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          autoHeight
          {...gridTestProps()}
          localeText={{ noRowsLabel: 'No promotions match these filters.' }}
        />
      </Box>

      <PromotionDialog
        open={dialog.open}
        initial={dialog.initial}
        onClose={() => setDialog({ open: false })}
        onSaved={() => setDialog({ open: false })}
      />

      <Dialog open={confirm !== null} onClose={busy ? undefined : () => setConfirm(null)}>
        <DialogTitle>
          {confirm?.kind === 'delete' ? `Delete ${confirm.row.name}?` : confirm?.row.active ? `Deactivate ${confirm?.row.name}?` : `Activate ${confirm?.row.name}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.kind === 'delete'
              ? 'Only a promotion with no codes and no redemptions can be deleted; otherwise deactivate it.'
              : confirm?.row.active
                ? 'Customers will no longer be able to redeem its codes until it is activated again.'
                : 'Its codes become redeemable again (subject to the dates and limits).'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={confirm?.kind === 'delete' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => {
              if (!confirm) return;
              if (confirm.kind === 'delete') remove.mutate(confirm.row);
              else toggle.mutate(confirm.row);
            }}
          >
            {confirm?.kind === 'delete' ? 'Delete' : confirm?.row.active ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromotionsList;
